import { useGetStockQuery } from "@fero/hooks";
import type { AttributeSelection } from "@fero/pages";
import {
  DeleteOutline,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { Holding } from "@physical-stock/core/shared/holding.type";
import type {
  OrganisationId,
  StorageLocationId,
} from "adl-gen/ferovinum/app/db";
import { RoundingInput } from "components/widgets/inputs/rounding-input/rounding-input";
import { format } from "date-fns";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

interface ProductWithSelectedQuantities {
  productId: string;
  productName: string;
  quantityRequired: number;
  unitType: string;
  attributeSelection: AttributeSelection;
}

interface AttributeConfig {
  attributeKey: "batch_number" | "best_before" | "rotation_number"; // The property name in the API data
  displayName: string; // Display name for the UI (e.g., "Batch", "Best Before")
  additionalInfoKey?: keyof Holding; // Optional additional info field
  additionalInfoLabel?: string; // Label for the additional info (e.g., "Best Before")
}

interface QuantityPerAttributeCardProps {
  productsWithSelectedQuantities: ProductWithSelectedQuantities[];
  attributeConfig: AttributeConfig;
  onAttributeQuantityAdd: (
    productId: string,
    attribute: string,
    quantity: number,
    batchNumber?: string,
    bestBefore?: string,
    rotationNumber?: string,
  ) => void;
  onAttributeQuantityChange: (
    productId: string,
    attribute: string,
    quantity: number,
  ) => void;
  onAttributeQuantityRemove: (productId: string, attribute: string) => void;
  organisationId: OrganisationId;
  storageLocationId: StorageLocationId;
  holdId?: string;
  releaseId?: string;
}

interface AvailableQuantities {
  [productId: string]: {
    totalAvailable: number;
    [attribute: string]: number;
  };
}

export const QuantityAttributeSelectionTable = ({
  productsWithSelectedQuantities,
  attributeConfig,
  onAttributeQuantityAdd,
  onAttributeQuantityChange,
  onAttributeQuantityRemove,
  organisationId,
  storageLocationId,
  holdId,
  releaseId,
}: QuantityPerAttributeCardProps) => {
  const {
    data: availableStock = [],
    isLoading: isAvailableStockLoading,
    isError: isAvailableStockError,
  } = useGetStockQuery(organisationId, storageLocationId, holdId, releaseId);

  const availableQuantities = useMemo(
    () =>
      availableStock.reduce<AvailableQuantities>((acc, item) => {
        const product = acc[item.product_id] ?? { totalAvailable: 0 };
        const attribute = item[attributeConfig.attributeKey];
        if (attribute) {
          product.totalAvailable += Number(item.quantity);
          product[attribute] =
            (product[attribute] ?? 0) + Number(item.quantity);
        }
        acc[item.product_id] = product;
        return acc;
      }, {}),
    [availableStock, attributeConfig],
  );

  if (isAvailableStockLoading || isAvailableStockError) return <>Loading...</>;

  return (
    <Grid container spacing={0}>
      <TableContainer elevation={0} component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "65%" }}>Product</TableCell>
              <TableCell sx={{ width: "10%", minWidth: "252px" }}>
                {attributeConfig.displayName}
                {attributeConfig.additionalInfoLabel &&
                  ` (${attributeConfig.additionalInfoLabel})`}
              </TableCell>
              <TableCell sx={{ width: "10%" }} align="right">
                Available
              </TableCell>
              <TableCell sx={{ width: "10%" }} align="right">
                Quantity
              </TableCell>
              <TableCell sx={{ width: "5%", minWidth: "48px" }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {productsWithSelectedQuantities.map((product) => (
              <ExpandableRow
                key={product.productId}
                row={product}
                availableQuantities={availableQuantities}
                onAttributeQuantityChange={onAttributeQuantityChange}
                onAttributeQuantityAdd={onAttributeQuantityAdd}
                onAttributeQuantityRemove={onAttributeQuantityRemove}
                availableStock={availableStock}
                attributeConfig={attributeConfig}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

interface ExpandableRowProps {
  row: ProductWithSelectedQuantities;
  availableQuantities: AvailableQuantities;
  onAttributeQuantityChange: (
    productId: string,
    attribute: string,
    quantity: number,
  ) => void;
  onAttributeQuantityAdd: (
    productId: string,
    attribute: string,
    quantity: number,
    batchNumber?: string,
    bestBefore?: string,
    rotationNumber?: string,
  ) => void;
  onAttributeQuantityRemove: (productId: string, attribute: string) => void;
  attributeConfig: AttributeConfig;
  availableStock: Holding[];
}

function ExpandableRow({
  row,
  availableQuantities,
  onAttributeQuantityChange,
  onAttributeQuantityAdd,
  onAttributeQuantityRemove,
  availableStock,
  attributeConfig,
}: ExpandableRowProps) {
  const selectedAttributeValues = Object.keys(row.attributeSelection);
  const [open, setOpen] = useState(true);
  const [selectedAttribute, setSelectedAttribute] = useState<Holding | null>(
    null,
  );
  const autocompleteRef = useRef<HTMLInputElement>(null);

  const availableAttributes = useMemo(() => {
    const filteredItems = availableStock.filter((item) => {
      if (item.product_id !== row.productId) return false;
      const attribute = item[attributeConfig.attributeKey];
      return (
        attribute && !selectedAttributeValues.includes(attribute.toString())
      );
    });

    return filteredItems.sort((a, b) => {
      const valueA = a[attributeConfig.attributeKey]?.toString() ?? "";
      const valueB = b[attributeConfig.attributeKey]?.toString() ?? "";
      return valueA.localeCompare(valueB);
    });
  }, [
    availableStock,
    row.productId,
    selectedAttributeValues,
    attributeConfig.attributeKey,
  ]);

  useEffect(() => {
    const hasNoSelection = Object.keys(row.attributeSelection).length === 0;
    const isExactlyOneAvailable = availableAttributes.length === 1;

    if (hasNoSelection && isExactlyOneAvailable) {
      const onlyOption = availableAttributes[0];
      const attributeValue =
        onlyOption[attributeConfig.attributeKey]?.toString();

      if (attributeValue) {
        onAttributeQuantityAdd(
          row.productId,
          attributeValue,
          0,
          onlyOption.batch_number ?? undefined,
          onlyOption.best_before ?? undefined,
          onlyOption.rotation_number ?? undefined,
        );
      }
    }
  }, [
    attributeConfig.attributeKey,
    availableAttributes,
    onAttributeQuantityAdd,
    row.attributeSelection,
    row.productId,
  ]);

  const handleAttributeSelect = (
    _event: React.SyntheticEvent,
    selectedItem: Holding | null,
  ) => {
    if (selectedItem) {
      const attribute =
        selectedItem[attributeConfig.attributeKey]?.toString() || "";
      onAttributeQuantityAdd(
        row.productId,
        attribute,
        0,
        selectedItem.batch_number ?? undefined,
        selectedItem.best_before ?? undefined,
        selectedItem.rotation_number ?? undefined,
      );
      setSelectedAttribute(null);
      if (autocompleteRef.current) autocompleteRef.current.blur();
    }
  };

  const handleQuantityChange = (attribute: string, quantity: number) => {
    const maxAvailable = availableQuantities[row.productId]?.[attribute] || 0;
    const otherAttributesTotal = Object.entries(row.attributeSelection)
      .filter(([val]) => val !== attribute)
      .reduce((sum, [, sel]) => sum + Number(sel.quantity), 0);
    const remainingRequired = row.quantityRequired - otherAttributesTotal;
    const validQuantity = Math.min(quantity, maxAvailable, remainingRequired);
    onAttributeQuantityChange(row.productId, attribute, validQuantity);
  };

  const getAdditionalInfo = (selection: {
    batchNumber?: string;
    quantity: string;
    bestBefore?: Date;
    rotationNumber?: string;
    rotationLine?: string;
  }): string => {
    // Map the selection properties to match the snake_case structure
    const mappedSelection: {
      batch_number?: string;
      best_before?: Date;
      rotation_number?: string;
    } = {
      batch_number: selection.batchNumber,
      best_before: selection.bestBefore,
      rotation_number: selection.rotationNumber,
    };

    const additionalInfoKey =
      attributeConfig.additionalInfoKey as keyof typeof mappedSelection;
    const additionalInfo =
      additionalInfoKey && mappedSelection[additionalInfoKey];
    const formattedDate =
      additionalInfo instanceof Date
        ? `${format(additionalInfo, "dd MMM yyyy")}`
        : String(additionalInfo);
    return additionalInfo ? formattedDate : "";
  };

  const getAutocompleteAttributeInfo = (
    option: {
      batch_number?: string | null;
      rotation_number?: string | null;
      best_before?: string | Date | null;
    },
    config: AttributeConfig,
  ) => {
    const mainValue = option[config.attributeKey]?.toString() || "N/A";
    const additionalInfo = option.best_before
      ? format(new Date(option.best_before), "dd MMM yyyy")
      : "N/A";

    return { mainValue, additionalInfo };
  };

  if (!availableQuantities?.[row.productId]?.totalAvailable) return null;
  const totalSelected = Object.entries(row.attributeSelection).reduce(
    (sum, [, sel]) => sum + Number(sel.quantity),
    0,
  );

  return (
    <>
      <TableRow>
        <TableCell sx={{ width: "65%", minWidth: "294px" }}>
          <Typography>{row.productName}</Typography>
        </TableCell>
        <TableCell sx={{ width: "10%", minWidth: "252px" }} />
        <TableCell align="right" sx={{ width: "10%", minWidth: "112px" }} />
        <TableCell align="right" sx={{ width: "10%", minWidth: "172px" }}>
          <Typography>
            {totalSelected} / {row.quantityRequired} {row.unitType}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: "5%", minWidth: "48px" }}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
      </TableRow>

      {open &&
        Object.entries(row.attributeSelection).map(([attribute, selection]) => {
          return (
            <TableRow key={attribute}>
              <TableCell sx={{ width: "65%" }} />
              <TableCell sx={{ width: "10%" }}>
                <AttributeValue
                  mainValue={attribute}
                  additionalInfo={getAdditionalInfo(selection)}
                />
              </TableCell>
              <TableCell align="right" sx={{ width: "10%" }}>
                <Typography>
                  {availableQuantities[row.productId][attribute]} {row.unitType}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ width: "10%" }}>
                <RoundingInput
                  value={
                    Number(selection.quantity) === 0 ? "" : selection.quantity
                  }
                  onChange={(e) =>
                    handleQuantityChange(attribute, Number(e.target.value))
                  }
                  onFocus={(e) => {
                    if (Number(selection.quantity) === 0) {
                      e.target.value = "";
                    }
                  }}
                  precision={0}
                  endAdornment={row.unitType}
                />
              </TableCell>
              <TableCell sx={{ width: "5%", minWidth: "48px" }}>
                <IconButton
                  size="small"
                  onClick={() =>
                    onAttributeQuantityRemove(row.productId, attribute)
                  }
                >
                  <DeleteOutline />
                </IconButton>
              </TableCell>
            </TableRow>
          );
        })}

      {availableAttributes.length !== 0 && open && (
        <TableRow>
          <TableCell />
          <TableCell>
            <Autocomplete
              id={`attribute-autocomplete-${row.productId}`}
              options={availableAttributes}
              getOptionLabel={(option) => {
                const { mainValue, additionalInfo } =
                  getAutocompleteAttributeInfo(option, attributeConfig);
                return `${mainValue} (${additionalInfo})`;
              }}
              renderOption={(props, option) => {
                const { mainValue, additionalInfo } =
                  getAutocompleteAttributeInfo(option, attributeConfig);
                return (
                  <li {...props}>
                    <AttributeValue
                      mainValue={mainValue}
                      additionalInfo={additionalInfo}
                    />
                  </li>
                );
              }}
              value={selectedAttribute}
              onChange={handleAttributeSelect}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={`Add New ${attributeConfig.displayName}`}
                  size="medium"
                  inputRef={autocompleteRef}
                />
              )}
              fullWidth
              disabled={availableAttributes.length === 0}
            />
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell />
        </TableRow>
      )}
    </>
  );
}

const AttributeValue = ({
  mainValue,
  additionalInfo,
}: {
  mainValue: React.ReactNode;
  additionalInfo?: React.ReactNode;
}) => (
  <Typography>
    <Box component="span" fontWeight="bold">
      {mainValue ?? "N/A"}
    </Box>
    {additionalInfo ? ` (${additionalInfo})` : ""}
  </Typography>
);
